import { stockyApi } from '../misc/StockyApi';

const ANALYSIS_DELAY_MS = 850;

const MARKETPLACE_LABELS = {
  mercado_libre: 'Mercado Libre',
  tienda_nube: 'Tienda Nube',
};

const STATUS_LABELS = {
  active: { text: 'active', color: 'green' },
  paused: { text: 'paused', color: 'yellow' },
  under_review: { text: 'under_review', color: 'orange' },
  omitted: { text: 'omitido', color: 'grey' },
  error: { text: 'error', color: 'red' },
  created: { text: 'creada', color: 'green' },
  pending: { text: 'pending', color: 'blue' },
  completed_with_errors: { text: 'completado con errores', color: 'orange' },
  needs_review: { text: 'requiere revisión', color: 'orange' },
  retrying: { text: 'reintentando', color: 'blue' },
};

const jobStore = new Map();

function clone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function normalizeString(value) {
  return String(value || '').trim();
}

function hasSingleDestinationSeller(payload = {}) {
  const sellerIds = Array.isArray(payload?.sellers)
    ? Array.from(
        new Set(
          payload.sellers
            .map((value) => normalizeString(value))
            .filter(Boolean),
        ),
      )
    : [];
  return sellerIds.length === 1;
}

function normalizeMarketplace(marketplace) {
  return normalizeString(marketplace).toLowerCase();
}

function normalizeCopyNotice(copyNotice) {
  const type = normalizeString(copyNotice?.type).toUpperCase();
  if (type === 'KIT_LINKED') return { type: 'KIT_LINKED' };
  if (type === 'KIT') return { type: 'KIT' };
  if (type !== 'PACK') return undefined;
  const sourceSaleUnitQuantity = Number.parseInt(copyNotice?.sourceSaleUnitQuantity, 10);
  if (!Number.isSafeInteger(sourceSaleUnitQuantity) || sourceSaleUnitQuantity < 2) {
    return undefined;
  }
  return { type: 'PACK', sourceSaleUnitQuantity };
}

function resolveAccountMarketplace(account = {}) {
  return normalizeMarketplace(account?.accountType || account?.account_type);
}

function resolveMarketplaceLabel(marketplace) {
  return MARKETPLACE_LABELS[normalizeMarketplace(marketplace)] || 'Canal';
}

function resolvePublicationStatusTone(status) {
  const normalized = normalizeString(status).toLowerCase();
  return STATUS_LABELS[normalized] || STATUS_LABELS.omitted;
}

function buildReviewUrl(rowId, sku, account, listingId) {
  const params = new URLSearchParams();
  const normalizedSku = normalizeString(sku);
  const normalizedSellerId = normalizeString(account?.id);
  const normalizedListingId = normalizeString(listingId || rowId);

  if (normalizedSku) params.set('sku', normalizedSku);
  if (normalizedSellerId) params.set('sellerId', normalizedSellerId);
  if (normalizedListingId) params.set('listingId', normalizedListingId);

  const query = params.toString();
  return query ? `/publicaciones?${query}` : '/publicaciones';
}

function getSelectedRowById(selectedRows, rowId) {
  return (
    (Array.isArray(selectedRows) ? selectedRows : []).find(
      (row) => normalizeString(row?.id) === normalizeString(rowId),
    ) || null
  );
}

function resolveStatusRank(status) {
  const normalized = normalizeString(status).toLowerCase();
  if (normalized === 'active') return 4;
  if (normalized === 'under_review') return 3;
  if (normalized === 'paused') return 2;
  if (normalized === 'closed') return 1;
  return 0;
}

function resolveTimestamp(value) {
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizePublication(publication = {}, row = {}) {
  const catalogListing =
    publication.catalogListing === true ||
    publication.catalog_listing === true ||
    publication.isCatalog === true ||
    row.catalogListing === true ||
    row.catalog_listing === true;
  const catalogProductId = normalizeString(
    publication.catalogProductId ||
      publication.catalog_product_id ||
      row.catalogProductId ||
      row.catalog_product_id ||
      row?.primaryListingData?._raw?.listing?.catalog_product_id ||
      row?.primaryListingData?._raw?.listing?.catalogProductId ||
      row?.primaryListingData?._raw?.listing?.integrationMetadata?.mercadoLibre?.catalogProductId,
  );

  return {
    id: normalizeString(publication.id),
    title: normalizeString(publication.title) || normalizeString(row.title) || 'Publicacion',
    channel: normalizeMarketplace(publication.marketplace || row.marketplace),
    accountId: normalizeString(publication.sellerId || row.sellerId),
    accountName: normalizeString(publication.sellerNickname || row.sellerNickname) || 'Cuenta',
    status: normalizeString(publication.status || row.status || 'active').toLowerCase(),
    updatedAt:
      normalizeString(
        publication.updatedAt ||
          row.listingUpdatedAt ||
          row.lastComputedAt ||
          row.listingCreatedAt,
      ) || null,
    listingId: normalizeString(publication.id || row.listingId),
    marketplaceId: normalizeString(
      publication.marketplaceId ||
        row.marketplaceId ||
        row?.primaryListingData?._raw?.listing?.marketplaceId,
    ),
    listingTypeId: normalizeString(
      publication.listingTypeId ||
        row.listingTypeId ||
        row?.primaryListingData?._raw?.listing?.listingTypeId,
    ),
    categoryId: normalizeString(
      publication.categoryId ||
        publication.category_id ||
        row.categoryId ||
        row?.primaryListingData?._raw?.listing?.categoryId ||
        row?.primaryListingData?._raw?.listing?.category_id,
    ),
    sku: normalizeString(publication.sku || row.sku),
    permalink: normalizeString(publication.permalink || row.permalink),
    userProductId: normalizeString(
      publication.userProductId ||
        row.userProductId ||
        row?.primaryListingData?._raw?.listing?.integrationMetadata?.mercadoLibre?.userProductId,
    ),
    catalogProductId,
    // A catalog product reference alone does not make the source a catalog listing.
    catalogListing,
  };
}

function chooseSourcePublication(row = {}) {
  const publications = Array.isArray(row.listings) ? row.listings : [];
  if (publications.length === 0) return null;

  return publications
    .map((publication) => normalizePublication(publication, row))
    .sort((a, b) => {
      const rankDiff = resolveStatusRank(b.status) - resolveStatusRank(a.status);
      if (rankDiff !== 0) return rankDiff;
      const timeDiff = resolveTimestamp(b.updatedAt) - resolveTimestamp(a.updatedAt);
      if (timeDiff !== 0) return timeDiff;
      return a.title.localeCompare(b.title);
    })[0];
}

export function resolveSourceProductReference(marketplaceId) {
  return normalizeString(marketplaceId).split('-', 2)[0];
}

function buildProductKey(accountId, marketplaceId) {
  return `${normalizeString(accountId)}:${resolveSourceProductReference(marketplaceId)}`;
}

/**
 * La unidad de copia es la publicación de origen (el producto padre), no la variante:
 * varias variantes seleccionadas del mismo ítem se copian una sola vez.
 */
export function groupListingsBySourceProduct(listings = []) {
  const groups = new Map();

  (Array.isArray(listings) ? listings : []).forEach((listing) => {
    if (!listing || listing.selected === false) return;
    const marketplaceId = normalizeString(listing.marketplaceId || listing.listingId);
    const accountId = normalizeString(listing.accountId);
    const productKey = listing.productKey || buildProductKey(accountId, marketplaceId);
    const current = groups.get(productKey);

    if (!current) {
      groups.set(productKey, {
        productKey,
        accountId,
        accountName: listing.accountName,
        channel: listing.channel,
        title: listing.title,
        sku: listing.sku,
        rowId: listing.rowId,
        catalogListing: listing.catalogListing === true,
        productReference: resolveSourceProductReference(marketplaceId),
        listingIds: [listing.listingId].filter(Boolean),
        variantCount: 1,
        sourcePublication: listing.sourcePublication,
      });
      return;
    }

    if (listing.listingId && !current.listingIds.includes(listing.listingId)) {
      current.listingIds.push(listing.listingId);
    }
    current.variantCount += 1;
  });

  return Array.from(groups.values());
}

export function countCopyUnits(listings = []) {
  return groupListingsBySourceProduct(listings).length;
}

export function buildTiendaNubeOptionsPayload(options = {}) {
  return {
    publishState: options.publishState === 'draft' ? 'draft' : 'published',
    priceAdjustmentPercent: Number.isFinite(Number(options.priceAdjustmentPercent))
      ? Number(options.priceAdjustmentPercent)
      : 0,
    copyPromotionalPrice: options.copyPromotionalPrice !== false,
    copyImages: options.copyImages !== false,
    copyDescription: options.copyDescription !== false,
    appendSpecsToDescription: options.appendSpecsToDescription !== false,
    copyFreeShipping: options.copyFreeShipping === true,
    stockSource: options.stockSource === 'zero' ? 'zero' : 'mercury',
  };
}

function buildPreviewListing(publication = {}, row = {}) {
  const normalized = normalizePublication(publication, row);
  return {
    productKey: buildProductKey(normalized.accountId, normalized.marketplaceId),
    listingId: normalized.id || normalizeString(publication?.id || row?.listingId),
    rowId: normalizeString(row?.id),
    sku: normalizeString(row?.sku),
    title: normalized.title || normalizeString(row?.title) || 'Publicacion',
    catalogListing: normalized.catalogListing === true,
    channel: normalized.channel || normalizeMarketplace(row?.marketplace),
    accountName: normalized.accountName || normalizeString(row?.sellerNickname) || 'Cuenta',
    accountId: normalized.accountId || normalizeString(row?.sellerId),
    status: normalized.status || 'active',
    permalink: normalized.permalink || normalizeString(row?.permalink),
    marketplaceId: normalized.marketplaceId || normalizeString(publication?.id || row?.listingId),
    listingTypeId: normalized.listingTypeId || '',
    categoryId: normalized.categoryId || '',
    selected: true,
    sourcePublication: normalized,
  };
}

function buildPublishRequestPayload(selectedRow = {}, sourcePublication = {}, destinationAccount = {}, options = {}) {
  const primary = selectedRow?.primaryListingData || {};
  const rawItem = primary?._raw?.item || {};
  const rawListing = primary?._raw?.listing || {};
  const rawVariation = primary?._raw?.variation || null;
  const rawMlMetadata = rawListing?.integrationMetadata?.mercadoLibre || {};
  const userProductId = normalizeString(
    rawMlMetadata.userProductId ||
      rawListing.user_product_id ||
      rawItem.user_product_id ||
      '',
  );
  const catalogListing =
    rawListing.catalogListing === true ||
    rawListing.catalog_listing === true ||
    sourcePublication.catalogListing === true;
  const userProductListing = Boolean(userProductId) || catalogListing;
  const rawTags = Array.isArray(rawListing.tags) ? rawListing.tags : [];
  const tags = Array.from(
    new Set(
      [...rawTags, ...(userProductListing ? ['user_product_listing'] : [])]
        .map((value) => normalizeString(value))
        .filter(Boolean),
    ),
  );

  const title = normalizeString(
    rawListing.title ||
      primary.title ||
      selectedRow.title ||
      sourcePublication.title ||
      rawItem.title,
  );
  const categoryId = normalizeString(
    rawListing.categoryId ||
      rawListing.category_id ||
      primary.categoryId ||
      rawItem.categoryId ||
      rawItem.category_id,
  );
  const attributes =
    (Array.isArray(rawListing.attributes) && rawListing.attributes.length > 0 && rawListing.attributes) ||
    (Array.isArray(rawItem.attributes) && rawItem.attributes.length > 0 && rawItem.attributes) ||
    [];
  const pictures =
    (Array.isArray(rawListing.pictures) && rawListing.pictures.length > 0 && rawListing.pictures) ||
    (Array.isArray(rawItem.pictures) && rawItem.pictures.length > 0 && rawItem.pictures) ||
    [];
  const variations =
    (Array.isArray(rawListing.variations) && rawListing.variations.length > 0 && rawListing.variations) ||
    (Array.isArray(rawItem.variations) && rawItem.variations.length > 0 && rawItem.variations) ||
    (rawVariation ? [rawVariation] : []);
  const saleTerms =
    (Array.isArray(rawListing.sale_terms) && rawListing.sale_terms.length > 0 && rawListing.sale_terms) ||
    (Array.isArray(rawItem.sale_terms) && rawItem.sale_terms.length > 0 && rawItem.sale_terms) ||
    [];
  const listingTypeId = normalizeString(
    rawListing.listingTypeId ||
      rawListing.listing_type_id ||
      sourcePublication.listingTypeId ||
      primary.listingTypeId ||
      '',
  );
  const sourceMarketplaceId = normalizeString(
    sourcePublication.marketplaceId ||
      rawListing.marketplaceId ||
      primary?._raw?.listing?.marketplaceId ||
      '',
  );
  const sourceSellerId = normalizeString(
    sourcePublication.accountId ||
      rawListing.sellerId ||
      primary?._raw?.listing?.sellerId ||
      '',
  );

  const payload = {
    title,
    category_id: categoryId,
    price: Number(rawListing.price ?? primary.price ?? rawItem.price ?? 0),
    attributes,
    pictures,
    variations,
    sellers: [normalizeString(destinationAccount.id)],
    listing_type_id: listingTypeId,
    stock: Number(primary.availableQuantity ?? rawItem.availableQuantity ?? 0),
    sku: normalizeString(rawItem.sku || primary.sku || selectedRow.sku || sourcePublication.sku),
    description: String(rawListing.description || rawItem.description || '').trim(),
    tags,
    user_product_id: userProductId || sourcePublication.userProductId || undefined,
    catalog_listing: catalogListing || sourcePublication.catalogListing || undefined,
    sale_terms: saleTerms,
    source_marketplace_id: sourceMarketplaceId || undefined,
    source_seller_id: sourceSellerId || undefined,
    flow: 'publish_in_another_account',
    publishOptions: clone(options || {}),
  };

  if (!payload.category_id) delete payload.category_id;
  if (!payload.listing_type_id) delete payload.listing_type_id;
  if (!payload.description) delete payload.description;
  if (!Array.isArray(payload.tags) || payload.tags.length === 0) delete payload.tags;
  if (!payload.user_product_id) delete payload.user_product_id;
  if (!payload.catalog_listing) delete payload.catalog_listing;
  if (!Array.isArray(payload.attributes) || payload.attributes.length === 0) delete payload.attributes;
  if (!Array.isArray(payload.pictures) || payload.pictures.length === 0) delete payload.pictures;
  if (!Array.isArray(payload.variations) || payload.variations.length === 0) delete payload.variations;
  if (!Array.isArray(payload.sale_terms) || payload.sale_terms.length === 0) delete payload.sale_terms;
  if (!payload.source_marketplace_id) delete payload.source_marketplace_id;
  if (!payload.source_seller_id) delete payload.source_seller_id;

  return payload;
}

export function groupDestinationAccounts(accounts = [], channel = '') {
  const normalizedChannel = normalizeMarketplace(channel);
  return (Array.isArray(accounts) ? accounts : [])
    .filter((account) => resolveAccountMarketplace(account) === normalizedChannel)
    .map((account) => {
      const accountType = resolveAccountMarketplace(account);
      return {
        id: normalizeString(account.id),
        accountType,
        name: normalizeString(account.nickname) || 'Cuenta destino',
        label: normalizeString(account.nickname) || 'Cuenta destino',
        statusLabel: resolveMarketplaceLabel(accountType),
        raw: account,
      };
    })
    .filter((account) => account.id.length > 0);
}

export async function analyzeSelectedSkus(selectedSkuIds = [], selectedRows = []) {
  const normalizedIds = new Set(
    (Array.isArray(selectedSkuIds) ? selectedSkuIds : [])
      .map((value) => normalizeString(value))
      .filter(Boolean),
  );
  const selected = (Array.isArray(selectedRows) ? selectedRows : []).filter(
    (row) => normalizedIds.size === 0 || normalizedIds.has(normalizeString(row?.id)),
  );

  return new Promise((resolve) => {
    globalThis.setTimeout(() => {
      const sourcePublications = [];
      let activeCount = 0;
      let pausedCount = 0;
      let withoutPublicationsCount = 0;

      selected.forEach((row) => {
        const publications = Array.isArray(row.listings) ? row.listings : [];
        publications.forEach((publication) => {
          const status = normalizeString(publication.status || row.status || 'active').toLowerCase();
          if (status === 'active') activeCount += 1;
          if (status === 'paused') pausedCount += 1;
          sourcePublications.push(buildPreviewListing(publication, row));
        });

        if (publications.length === 0) {
          withoutPublicationsCount += 1;
        }
      });

      resolve({
        skuCount: selected.length,
        activeCount,
        pausedCount,
        withoutPublicationsCount,
        copyCount: sourcePublications.length,
        sourcePublications,
        previewListings: sourcePublications,
      });
    }, ANALYSIS_DELAY_MS);
  });
}

function buildJobTargets(payload = {}) {
  const sourcePublications = Array.isArray(payload?.selectedListings)
    ? payload.selectedListings
    : Array.isArray(payload?.analysis?.previewListings)
      ? payload.analysis.previewListings
      : Array.isArray(payload?.analysis?.sourcePublications)
        ? payload.analysis.sourcePublications
        : [];
  const selectedAccounts = Array.isArray(payload?.selectedAccounts) ? payload.selectedAccounts : [];
  const selectedRows = Array.isArray(payload?.selectedRows) ? payload.selectedRows : [];
  const targets = [];
  const selectableListings = sourcePublications.filter(
    (entry) => entry && entry.selected !== false && entry.sourcePublication,
  );
  const listingsByProduct = new Map(
    groupListingsBySourceProduct(selectableListings).map((group) => [group.productKey, group]),
  );
  const representatives = new Map();
  selectableListings.forEach((entry) => {
    const productKey =
      entry.productKey || buildProductKey(entry.accountId, entry.marketplaceId || entry.listingId);
    if (!representatives.has(productKey)) {
      representatives.set(productKey, entry);
    }
  });

  Array.from(representatives.entries()).forEach(([productKey, entry]) => {
    const group = listingsByProduct.get(productKey);

    selectedAccounts.forEach((account) => {
      const accountMarketplace = resolveAccountMarketplace(account);
      targets.push({
        sku: entry.sku,
        rowId: entry.rowId,
        channel: accountMarketplace,
        productKey,
        variantCount: Number(group?.variantCount || 1),
        sourceListingIds: clone(group?.listingIds || [entry.listingId].filter(Boolean)),
        sourceRow: clone(getSelectedRowById(selectedRows, entry.rowId)),
        sourcePublication: clone(entry.sourcePublication),
        publishRequestPayload: clone(
          entry.publishRequestPayload ||
            buildPublishRequestPayload(
              clone(getSelectedRowById(selectedRows, entry.rowId)) || {},
              entry.sourcePublication,
              account,
              payload?.options || {},
            ),
        ),
        destinationAccount: {
          id: normalizeString(account?.id),
          name: normalizeString(account?.name) || 'Cuenta destino',
          accountType: accountMarketplace,
          statusLabel: account?.statusLabel || resolveMarketplaceLabel(accountMarketplace),
        },
        status: 'pending',
        action: 'Se copiará',
        reason: '',
        resultLabel: 'Se copiará',
        publicationId: normalizeString(entry.listingId || entry.marketplaceId || entry.sourcePublication?.id),
        reviewUrl: buildReviewUrl(
          entry.rowId,
          entry.sku,
          account,
          entry.listingId || entry.marketplaceId || entry.sourcePublication?.id,
        ),
        marketplaceId: '',
        localSuccess: false,
        localError: '',
        error: '',
        completedAt: null,
      });
    });
  });

  return targets;
}

function createInitialJob(jobId, payload, targets) {
  const createdAt = new Date().toISOString();
  return {
    id: jobId,
    status: 'RUNNING',
    createdAt,
    updatedAt: createdAt,
    done: 0,
    totalToCreate: targets.length,
    payload: {
      authUser: clone(payload?.authUser || null),
      analysis: clone(payload?.analysis || null),
      selectedRows: clone(payload?.selectedRows || []),
      selectedListings: clone(payload?.selectedListings || []),
      selectedChannel: normalizeMarketplace(payload?.selectedChannel),
      selectedAccounts: (Array.isArray(payload?.selectedAccounts) ? payload.selectedAccounts : []).map((account) => ({
        id: normalizeString(account?.id),
        name: normalizeString(account?.name),
        accountType: normalizeMarketplace(account?.accountType),
        statusLabel: normalizeString(account?.statusLabel),
      })),
      options: clone(payload?.options || {}),
    },
    results: targets.map((target) => ({ ...target })),
    summary: {
      created: 0,
      omitted: targets.filter((target) => target.status === 'omitted').length,
      errors: 0,
    },
  };
}

function refreshJobSummary(job) {
  const results = Array.isArray(job.results) ? job.results : [];
  const reviewRequired = results.filter((result) => result.status === 'needs_review').length;
  return {
    created: results.filter((result) => result.status === 'created').length,
    omitted: results.filter((result) => result.status === 'omitted').length,
    errors: results.filter((result) => result.status === 'error').length,
    reviewRequired,
  };
}

const COMPATIBILITIES_ASYNC_THRESHOLD = 200;

/**
 * Resume en un aviso lo que pasó con las compatibilidades de autopartes. Devuelve
 * null cuando la copia no las intentó (la publicación original no las tiene).
 */
export function buildCompatibilitiesWarning(compatibilities) {
  if (!compatibilities || typeof compatibilities !== 'object') {
    return null;
  }
  const copied = Number(compatibilities.copied) || 0;
  const catalogManaged = Number(compatibilities.catalog_managed) || 0;
  const error = normalizeString(compatibilities.error || '');
  const copiedText = `${copied} ${copied === 1 ? 'compatibilidad' : 'compatibilidades'}`;

  if (error) {
    return {
      code: 'COMPATIBILITIES_COPY_FAILED',
      message:
        copied > 0
          ? `Se copiaron ${copiedText}, pero la copia de compatibilidades se interrumpió y puede haber quedado incompleta: ${error}`
          : `No se pudieron copiar las compatibilidades: ${error}`,
    };
  }

  const parts = [
    copied > 0
      ? `Se copiaron ${copiedText}.`
      : 'La publicación original no devolvió compatibilidades para copiar.',
  ];
  if (copied > COMPATIBILITIES_ASYNC_THRESHOLD) {
    parts.push('Mercado Libre puede tardar unos minutos en mostrarlas todas.');
  }
  if (catalogManaged > 0) {
    parts.push(
      `Mercado Libre administra otras ${catalogManaged} compatibilidades que no se pueden copiar.`,
    );
  }
  return { code: 'COMPATIBILITIES_COPIED', message: parts.join(' ') };
}

export function buildJobResultFromResponse(currentResult, responseResult) {
  const marketplacePermalink = normalizeString(
    responseResult?.data?.permalink ||
      responseResult?.data?.link ||
      responseResult?.permalink ||
      responseResult?.reviewUrl ||
      '',
  );
  const localListing = responseResult?.local_listing || null;
  const marketplaceId =
    normalizeString(responseResult?.marketplace_id) ||
    normalizeString(responseResult?.data?.id) ||
    normalizeString(responseResult?.id) ||
    normalizeString(localListing?.marketplaceId);
  const localSuccess = responseResult?.local_success !== false;
  const localError = normalizeString(responseResult?.local_error || '');
  const descriptionError = normalizeString(responseResult?.description_error || '');
  const marketplaceError = normalizeString(responseResult?.marketplace_error || '');
  const correction = responseResult?.correction || null;
  const retryable = Boolean(correction?.canRetry);
  const remoteSuccess = responseResult?.success !== false && !descriptionError && !retryable;
  const hasError = !remoteSuccess || !localSuccess;
  const status = retryable ? 'needs_review' : hasError ? 'error' : 'created';
  const resultLabel = retryable ? 'Requiere revisión' : hasError ? 'Error' : 'Se copió';
  const action = retryable ? 'Corregir' : resultLabel;
  const reason = retryable
    ? normalizeString(correction?.reason || correction?.message || currentResult.reason || '')
    : hasError
      ? marketplaceError
        ? `Mercado Libre rechazó la publicación: ${marketplaceError}. MercuryCom no creó la publicación.`
        : descriptionError
        ? `No se pudo copiar la descripción: ${descriptionError}`
        : `MercuryCom no pudo guardar la publicación localmente${localError ? `: ${localError}` : '.'}`
      : '';
  const reviewUrl = marketplacePermalink ||
    normalizeString(localListing?.permalink || localListing?.link) ||
    (marketplaceId
      ? buildReviewUrl(currentResult.rowId, currentResult.sku, currentResult.destinationAccount, marketplaceId)
      : '');
  const copyNotice = normalizeCopyNotice(responseResult?.copyNotice);
  const compatibilitiesWarning = buildCompatibilitiesWarning(responseResult?.compatibilities);

  return {
    ...currentResult,
    status,
    completedAt: new Date().toISOString(),
    marketplaceId,
    reviewUrl,
    localSuccess,
    localError,
    ...(compatibilitiesWarning ? { warnings: [compatibilitiesWarning] } : {}),
    marketplaceError: marketplaceError || undefined,
    descriptionError: descriptionError || undefined,
    correction: correction || undefined,
    reason,
    error: reason,
    action,
    resultLabel,
    ...(copyNotice ? { copyNotice } : {}),
  };
}

/**
 * Traduce la respuesta del copiado a Tienda Nube al mismo modelo de fila que usa el job
 * de publicación en Mercado Libre, para que la tabla de resultados sea una sola.
 */
export function buildJobResultFromCopyResponse(currentResult, responseData) {
  const status = normalizeString(responseData?.status).toUpperCase();
  const warnings = Array.isArray(responseData?.warnings) ? responseData.warnings : [];
  const blocking = Array.isArray(responseData?.blocking) ? responseData.blocking : [];
  const destinationProduct = responseData?.destinationProduct || null;
  const reviewUrl = normalizeString(
    destinationProduct?.permalink || destinationProduct?.adminUrl || '',
  );
  const variants = Array.isArray(responseData?.variants) ? responseData.variants : [];
  const errorMessage = normalizeString(
    responseData?.error?.message || blocking[0]?.message || '',
  );
  const copyNotice = normalizeCopyNotice(responseData?.copyNotice);

  if (status === 'CREATED') {
    return {
      ...currentResult,
      status: 'created',
      completedAt: new Date().toISOString(),
      marketplaceId: normalizeString(destinationProduct?.productId),
      reviewUrl,
      localSuccess: true,
      localError: '',
      // Los avisos viajan estructurados en `warnings`: la tabla los muestra en un
      // desplegable para no convertir la fila en un párrafo.
      reason: '',
      error: '',
      action: 'Se copió',
      resultLabel: variants.length > 1 ? `Se copió (${variants.length} variantes)` : 'Se copió',
      warnings,
      ...(copyNotice ? { copyNotice } : {}),
    };
  }

  if (status === 'SKIPPED_EXISTS') {
    return {
      ...currentResult,
      status: 'omitted',
      completedAt: new Date().toISOString(),
      marketplaceId: normalizeString(destinationProduct?.productId),
      reviewUrl,
      localSuccess: true,
      localError: '',
      reason: errorMessage || 'Ya existe un producto con ese SKU en la tienda destino.',
      error: '',
      action: 'Ya existía',
      resultLabel: 'Ya existía',
      warnings,
      ...(copyNotice ? { copyNotice } : {}),
    };
  }

  const unknownRemoteState = normalizeString(responseData?.remoteCreationState).toUpperCase() === 'UNKNOWN';

  return {
    ...currentResult,
    status: 'error',
    completedAt: new Date().toISOString(),
    reviewUrl: '',
    localSuccess: false,
    reason:
      errorMessage ||
      (unknownRemoteState
        ? 'No sabemos si Tienda Nube llegó a crear el producto. Verifica en tu tienda e intenta nuevamente.'
        : 'No se pudo copiar la publicación.'),
    error: errorMessage,
    action: 'Error',
    resultLabel: 'Error',
    warnings,
  };
}

function patchPublishJobResult(jobId, publicationId, patch) {
  const job = jobStore.get(jobId);
  if (!job) {
    return null;
  }

  const normalizedPublicationId = normalizeString(publicationId);
  const results = Array.isArray(job.results) ? [...job.results] : [];
  const index = results.findIndex(
    (result) =>
      normalizeString(result?.publicationId || result?.listingId || result?.marketplaceId || result?.id) ===
      normalizedPublicationId,
  );
  if (index < 0) {
    return clone(job);
  }

  results[index] = {
    ...results[index],
    ...clone(patch || {}),
  };
  const updatedJob = {
    ...job,
    results,
    updatedAt: new Date().toISOString(),
    summary: refreshJobSummary({ ...job, results }),
  };
  jobStore.set(jobId, updatedJob);
  return clone(updatedJob);
}

async function runPublishJob(jobId) {
  const job = jobStore.get(jobId);
  if (!job || job.status !== 'RUNNING') {
    return;
  }

  for (let index = 0; index < job.results.length; index += 1) {
    const currentJob = jobStore.get(jobId);
    if (!currentJob || currentJob.status !== 'RUNNING') {
      return;
    }

    const currentResult = currentJob.results[index];
    if (!currentResult) {
      continue;
    }

    const updatedResults = [...currentJob.results];

    try {
      if (normalizeMarketplace(currentResult.channel) === 'tienda_nube') {
        const response = await stockyApi.copyListing(currentJob.payload.authUser, {
          destination: {
            marketplace: 'tienda_nube',
            sellerId: normalizeString(currentResult.destinationAccount?.id),
          },
          sourceListingIds: Array.isArray(currentResult.sourceListingIds)
            ? currentResult.sourceListingIds
            : [],
          options: buildTiendaNubeOptionsPayload(currentJob.payload.options || {}),
          idempotencyKey: `${currentJob.id}:${currentResult.productKey || currentResult.publicationId}`,
        });
        updatedResults[index] = buildJobResultFromCopyResponse(currentResult, response?.data);
      } else {
        const selectedRow = getSelectedRowById(currentJob.payload.selectedRows, currentResult.rowId);
        const requestPayload = buildPublishRequestPayload(
          selectedRow || currentResult.sourceRow || {},
          currentResult.sourcePublication || {},
          currentResult.destinationAccount || {},
          currentJob.payload.options || {},
        );
        const response = await stockyApi.publishMarketplaceItemRaw(
          currentJob.payload.authUser,
          requestPayload,
        );
        const responseResult = Array.isArray(response?.data) ? response.data[0] : null;
        updatedResults[index] = buildJobResultFromResponse(currentResult, responseResult);
      }
    } catch (error) {
      updatedResults[index] = {
        ...currentResult,
        status: 'error',
        completedAt: new Date().toISOString(),
        error: error?.response?.data?.message || error?.message || 'No se pudo crear la publicación.',
        resultLabel: 'Error',
      };
    }

    const nextDone = index + 1;
    jobStore.set(jobId, {
      ...currentJob,
      done: nextDone,
      updatedAt: new Date().toISOString(),
      results: updatedResults,
      summary: refreshJobSummary({ ...currentJob, results: updatedResults }),
    });
  }

  const finalJob = jobStore.get(jobId);
  if (!finalJob) {
    return;
  }

  const finalSummary = refreshJobSummary(finalJob);
  jobStore.set(jobId, {
    ...finalJob,
    status:
      finalSummary.errors > 0 || finalSummary.reviewRequired > 0
        ? 'COMPLETED_WITH_ERRORS'
        : 'COMPLETED',
    done: finalJob.totalToCreate,
    completedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    summary: finalSummary,
  });
}

export async function retryPublicationInJob({
  jobId,
  publicationId,
  title,
  user,
  retryContext = {},
  jobSnapshot = null,
}) {
  const normalizedJobId = normalizeString(jobId);
  const normalizedPublicationId = normalizeString(publicationId);
  const normalizedTitle = normalizeString(title);
  if (!normalizedJobId || !normalizedPublicationId) {
    throw new Error('jobId and publicationId are required');
  }
  if (!normalizedTitle) {
    throw new Error('title is required');
  }

  const currentJob = jobStore.get(normalizedJobId) || clone(jobSnapshot);
  if (!currentJob) {
    throw new Error('Publish job not found');
  }
  if (!jobStore.has(normalizedJobId)) {
    jobStore.set(normalizedJobId, currentJob);
  }

  const currentResult = (Array.isArray(currentJob.results) ? currentJob.results : []).find(
    (result) =>
      normalizeString(result?.publicationId || result?.listingId || result?.marketplaceId || result?.id) ===
      normalizedPublicationId,
  );
  if (!currentResult) {
    throw new Error('Publication not found in job');
  }

  try {
    patchPublishJobResult(normalizedJobId, normalizedPublicationId, {
      status: 'retrying',
      action: 'Reintentando...',
      resultLabel: 'Reintentando...',
      reason: 'Reintentando...',
      error: '',
    });

    const retryPublishRequestPayloadBase =
      clone(retryContext.publishRequestPayload) ||
      clone(currentResult.publishRequestPayload) ||
      buildPublishRequestPayload(
        clone(retryContext.sourceRow || currentResult.sourceRow || {}),
        clone(retryContext.sourcePublication || currentResult.sourcePublication || {}),
        clone(retryContext.destinationAccount || currentResult.destinationAccount || {}),
        clone(retryContext.options || currentJob.payload?.options || {}),
      );
    const retryPublishRequestPayload = {
      ...retryPublishRequestPayloadBase,
      title: normalizedTitle,
    };
    if (!hasSingleDestinationSeller(retryPublishRequestPayload)) {
      throw new Error('No se pudo reintentar: falta la cuenta destino de la publicación.');
    }

    const requestBody = {
      publicationId: normalizedPublicationId,
      title: normalizedTitle,
      retryContext: {
        ...clone(retryContext || {}),
        publishRequestPayload: retryPublishRequestPayload,
      },
    };

    const response = await stockyApi.retryPublication(user, normalizedJobId, requestBody);
    const responseResult = response?.data?.result || response?.data?.data || response?.data || null;
    const updatedRow = buildJobResultFromResponse(
      {
        ...currentResult,
        status: 'retrying',
      },
      responseResult || {},
    );

    const updatedJob = patchPublishJobResult(normalizedJobId, normalizedPublicationId, updatedRow);
    const currentUpdatedJob = updatedJob || clone(jobStore.get(normalizedJobId));
    if (!currentUpdatedJob) {
      return null;
    }

    const finalSummary = refreshJobSummary(currentUpdatedJob);
    const finalStatus =
      finalSummary.errors > 0 || finalSummary.reviewRequired > 0 ? 'COMPLETED_WITH_ERRORS' : 'COMPLETED';
    const finalJob = {
      ...currentUpdatedJob,
      status: finalStatus,
      updatedAt: new Date().toISOString(),
      summary: finalSummary,
      done: currentUpdatedJob.done,
    };
    jobStore.set(normalizedJobId, finalJob);
    return clone(finalJob);
  } catch (error) {
    const fallbackReason = normalizeString(
      error?.response?.data?.message || error?.message || 'No se pudo reintentar la publicación.',
    );
    const retryableResult = currentResult.correction?.canRetry || currentResult.status === 'needs_review';
    const reverted = patchPublishJobResult(normalizedJobId, normalizedPublicationId, {
      status: retryableResult ? 'needs_review' : 'error',
      action: retryableResult ? 'Corregir' : 'Error',
      resultLabel: retryableResult ? 'Requiere revisión' : 'Error',
      reason: fallbackReason,
      error: fallbackReason,
    });
    if (reverted) {
      const revertedSummary = refreshJobSummary(reverted);
      const revertedStatus =
        revertedSummary.errors > 0 || revertedSummary.reviewRequired > 0
          ? 'COMPLETED_WITH_ERRORS'
          : 'COMPLETED';
      const revertedJob = {
        ...reverted,
        status: revertedStatus,
        updatedAt: new Date().toISOString(),
        summary: revertedSummary,
      };
      jobStore.set(normalizedJobId, revertedJob);
    }
    throw error;
  }
}

export async function createPublishJob(payload = {}) {
  const jobId = `publish-job-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const targets = buildJobTargets(payload);
  const job = createInitialJob(jobId, payload, targets);
  jobStore.set(jobId, job);

  if (targets.length === 0) {
    const completed = {
      ...job,
      status: 'COMPLETED',
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    jobStore.set(jobId, completed);
    return clone(completed);
  }

  globalThis.setTimeout(() => {
    void runPublishJob(jobId);
  }, 0);

  return clone(job);
}

export async function getPublishJobStatus(jobId) {
  return clone(jobStore.get(normalizeString(jobId)) || null);
}

export function getPublicationStatusMeta(status) {
  return resolvePublicationStatusTone(status);
}

export function resolvePublicationLabel(marketplace) {
  return resolveMarketplaceLabel(marketplace);
}
